import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ScrollUp from '../components/ScrollUp';
import './MiraSignalPage.css';

const appUrl = 'https://app.haider-masood.com';
const demoHref = '/#contact';

const trustIndicators = [
  'AI voice agents',
  'Human handoff',
  'Call summaries',
  'Audit trails',
  'Inbound + outbound',
  'Dashboard visibility',
];

const heroCards = [
  { label: 'Incoming call', value: 'Answered', icon: 'ri-phone-line', tone: 'teal' },
  { label: 'Intent detected', value: 'Scheduling', icon: 'ri-list-check-3', tone: 'blue' },
  { label: 'Follow-up queue', value: 'Ready', icon: 'ri-route-line', tone: 'green' },
  { label: 'Human handoff', value: 'Context attached', icon: 'ri-customer-service-2-line', tone: 'amber' },
  { label: 'Transcript', value: 'Saved', icon: 'ri-file-text-line', tone: 'teal' },
  { label: 'Audit trail', value: 'Logged', icon: 'ri-shield-check-line', tone: 'blue' },
];

const problemPoints = [
  {
    title: 'Missed calls become lost revenue',
    copy: 'A busy desk, lunch break, or after-hours ring can turn warm demand into voicemail.',
    icon: 'ri-phone-off-line',
  },
  {
    title: 'No consistent call record',
    copy: 'Important context lives in memory, sticky notes, and scattered follow-up messages.',
    icon: 'ri-file-damage-line',
  },
  {
    title: 'Responses vary by staff member',
    copy: 'Scripts, policy checks, and escalation rules change depending on who answers.',
    icon: 'ri-flow-chart',
  },
  {
    title: 'Follow-up is easy to forget',
    copy: 'Leads, patients, and customers often need one more reminder or routing step.',
    icon: 'ri-loop-left-line',
  },
  {
    title: 'Managers cannot see what happened',
    copy: 'Teams need call history and outcomes without listening to every recording.',
    icon: 'ri-dashboard-3-line',
  },
  {
    title: 'Compliance review arrives late',
    copy: 'Consent, handoff, DNC, and script adherence need a review layer from the start.',
    icon: 'ri-shield-check-line',
  },
];

const w6hCards = [
  {
    label: 'Who',
    title: 'Who is it for?',
    copy: 'For clinics, service businesses, agencies, appointment desks, and teams where missed calls directly create lost revenue.',
  },
  {
    label: 'What',
    title: 'What does MiraSignal do?',
    copy: 'An AI voice operations layer that answers, qualifies, routes, summarizes, audits, and logs calls.',
  },
  {
    label: 'Why',
    title: 'Why does it matter?',
    copy: 'Because phone calls usually have no memory. MiraSignal reduces missed calls, weak follow-up, and invisible revenue leakage.',
  },
  {
    label: 'When',
    title: 'When does it step in?',
    copy: 'After hours, during busy periods, when staff are unavailable, or when every call needs to be reviewed.',
  },
  {
    label: 'Where',
    title: 'Where does it fit?',
    copy: 'Between your phone number, AI voice agent, human operators, and management dashboard.',
  },
  {
    label: 'How',
    title: 'How does it work?',
    copy: 'Connect a number, configure scripts and routing, let AI handle routine calls, escalate edge cases, and review every call.',
  },
  {
    label: 'How much',
    title: 'How much does it cost?',
    copy: 'Packages are based on voice minutes, agents, storage, usage, support level, and call volume. No fake final pricing.',
  },
];

const lifecycleSteps = [
  {
    title: 'Connect number or intake workflow',
    copy: 'Bring the existing phone path into MiraSignal.',
    icon: 'ri-phone-line',
  },
  {
    title: 'Configure rules and scripts',
    copy: 'Set FAQs, routing, escalation, compliance language, and business hours.',
    icon: 'ri-settings-3-line',
  },
  {
    title: 'AI answers or places calls',
    copy: 'Handle inbound requests and outbound reminders.',
    icon: 'ri-headphone-line',
  },
  {
    title: 'Qualify and capture intent',
    copy: 'Identify need, urgency, contact context, and next action.',
    icon: 'ri-list-check-3',
  },
  {
    title: 'Summarize, transcribe, tag, and log',
    copy: 'Create a transcript, summary, outcome, tags, and next step.',
    icon: 'ri-file-list-3-line',
  },
  {
    title: 'Handoff when needed',
    copy: 'Escalate urgent or sensitive calls with context attached.',
    icon: 'ri-customer-service-2-line',
  },
  {
    title: 'Review operations',
    copy: 'Inspect history, compliance notes, performance, and missed-risk patterns.',
    icon: 'ri-bar-chart-box-line',
  },
  {
    title: 'Improve the workflow',
    copy: 'Use call data to refine scripts, qualification, and routing.',
    icon: 'ri-git-branch-line',
  },
];

const featureGroups = [
  {
    title: 'AI Call Handling',
    icon: 'ri-phone-line',
    features: ['Inbound answering', 'Outbound follow-up', 'Call qualification', 'Booking or routing'],
  },
  {
    title: 'Operator Control',
    icon: 'ri-dashboard-3-line',
    features: ['Live handoff', 'Operator visibility', 'Escalation status', 'Call queue state'],
  },
  {
    title: 'Memory and Review',
    icon: 'ri-file-text-line',
    features: ['Transcripts', 'Summaries', 'Call tags', 'Audit trails'],
  },
  {
    title: 'Compliance and Quality',
    icon: 'ri-shield-check-line',
    features: ['Script adherence', 'Review notes', 'Searchable history', 'Flagged calls'],
  },
];

const pricingInputs = [
  'Billable call minutes',
  'Inbound and outbound telephony',
  'Realtime voice model usage',
  'Post-call summaries',
  'Cloud Run compute',
  'Logs, database, and retention',
  'Support and onboarding',
  'Usage buffer and gross margin',
];

const pricingPlans = [
  {
    name: 'Starter',
    bestFor: 'Small teams testing AI call coverage',
    minutes: 'Limited monthly minutes after usage review',
    pricing: 'Starting from a scoped pilot',
    source: 'Calculated from usage profile',
    cta: 'Book a pilot',
    features: [
      'One AI receptionist workflow',
      'Inbound call handling',
      'Basic transcripts and summaries',
      'Basic dashboard visibility',
      'Email support',
    ],
    overage: 'Overages quoted per additional minute once provider costs are confirmed.',
  },
  {
    name: 'Growth',
    bestFor: 'Active businesses with daily call volume',
    minutes: 'Higher included minutes after call-volume estimate',
    pricing: 'Custom after volume estimate',
    source: 'Uses minute, voice, summary, and support assumptions',
    cta: 'Plan my workflow',
    features: [
      'Inbound and outbound workflows',
      'Call tagging and summaries',
      'Human handoff',
      'Compliance checks',
      'Follow-up workflows',
      'Priority support',
    ],
    overage: 'Overages modeled from billable minutes, SMS, and AI voice usage.',
  },
  {
    name: 'Operations',
    bestFor: 'Teams where calls are a serious operations channel',
    minutes: 'Operations allowance sized to departments or queues',
    pricing: 'Usage-based package design',
    source: 'Includes retention, audit, reporting, and support overhead',
    cta: 'Discuss operations',
    highlighted: true,
    features: [
      'Multiple workflows',
      'Multiple numbers or departments',
      'Advanced dashboard views',
      'Audit trails',
      'Team/operator visibility',
      'Advanced reporting',
      'Longer retention options',
    ],
    overage: 'Overage bands are set after reviewing monthly peak and average demand.',
  },
  {
    name: 'Enterprise / Agency',
    bestFor: 'Agencies, call centers, healthcare networks, and multi-location operations',
    minutes: 'Custom minutes and retention',
    pricing: 'Custom pricing',
    source: 'Final pricing requires security, support, and integration scope',
    cta: 'Scope enterprise',
    features: [
      'Multiple brands or clients',
      'Role-based access options',
      'Custom scripts',
      'SLA planning',
      'Dedicated onboarding',
      'Custom integrations',
      'Security/compliance review',
    ],
    overage: 'Commercial terms are set around expected volume, SLA, and integration needs.',
  },
];

// TODO: Replace these placeholders with real customer testimonials after pilots or signed references.
const testimonials = [
  {
    label: 'Example customer outcome',
    quote: 'We stopped losing calls after hours because every request had a next step by morning.',
    context: 'Clinic or appointment desk',
  },
  {
    label: 'Pilot-style testimonial placeholder',
    quote: 'Managers finally had a clean call history instead of relying on memory and scattered notes.',
    context: 'Service business',
  },
  {
    label: 'Example customer outcome',
    quote: 'The team no longer had to manually summarize every routine call before follow-up.',
    context: 'Agency operations team',
  },
  {
    label: 'Pilot-style testimonial placeholder',
    quote: 'Escalations became easier because every handoff included intent, urgency, and context.',
    context: 'Compliance-heavy team',
  },
];

const useCases = [
  {
    title: 'Clinics and appointment desks',
    copy: 'Capture appointment requests, route urgent calls, send reminders, and preserve reviewable records.',
    icon: 'ri-hospital-line',
  },
  {
    title: 'Real estate and property inquiries',
    copy: 'Qualify leads, answer common questions, tag urgency, and hand promising conversations to agents.',
    icon: 'ri-home-office-line',
  },
  {
    title: 'Agencies managing client calls',
    copy: 'Separate workflows by client, keep transcripts attached, and give account teams clean visibility.',
    icon: 'ri-briefcase-4-line',
  },
  {
    title: 'Service businesses',
    copy: 'Handle busy-hour overflow, collect job details, and route the right request to the right person.',
    icon: 'ri-tools-line',
  },
  {
    title: 'Follow-up and reminder campaigns',
    copy: 'Run outbound reminders, retries, status calls, and post-call tasks from one operations surface.',
    icon: 'ri-broadcast-line',
  },
  {
    title: 'Compliance-heavy teams',
    copy: 'Keep controlled scripts, audit-ready summaries, searchable history, and manager review queues.',
    icon: 'ri-shield-user-line',
  },
];

const dashboardStats = [
  { label: 'Active agents', value: '3', tone: 'teal' },
  { label: 'Voice minutes', value: '420', tone: 'blue' },
  { label: 'Team members', value: '8', tone: 'green' },
  { label: 'Review queue', value: '12', tone: 'amber' },
];

const dashboardRows = [
  { label: 'Sample caller', detail: 'New appointment request', status: 'Needs handoff', tone: 'amber' },
  { label: 'Follow-up due', detail: 'Outbound reminder queued', status: 'Queued', tone: 'teal' },
  { label: 'Compliance check passed', detail: 'Script adherence note attached', status: 'Clear', tone: 'green' },
  { label: 'Call summary generated', detail: 'Transcript, tags, and outcome saved', status: 'Logged', tone: 'blue' },
];

const dashboardUsageCards = [
  {
    title: 'Voice Plan',
    status: 'Active',
    rows: [
      ['Included minutes', '1,000'],
      ['Used minutes', '420'],
      ['Remaining minutes', '580'],
      ['Concurrent calls', 'Review in app'],
    ],
    progress: 42,
    note: 'Billing cycle based on usage profile',
  },
  {
    title: 'Agent Usage',
    status: 'Active',
    rows: [
      ['Configured agents', '3'],
      ['Inbound workflows', '2'],
      ['Outbound workflows', '1'],
      ['Remaining slots', 'Custom'],
    ],
    progress: 60,
    note: 'Workflow capacity depends on plan',
  },
  {
    title: 'Storage and Logs',
    status: 'Estimated',
    rows: [
      ['Transcripts', 'Reviewable'],
      ['Audit trails', 'Enabled'],
      ['Retention', 'Configurable'],
      ['Storage meter', 'Connected later'],
    ],
    progress: null,
    note: 'Final storage pricing needs usage data',
  },
];

const dashboardSupportCards = [
  {
    title: 'Live visibility',
    copy: 'See active agents, routed calls, review queues, and live work from one page.',
    icon: 'ri-pulse-line',
  },
  {
    title: 'Human handoff',
    copy: 'Escalate edge cases with caller intent, urgency, transcript, and summary attached.',
    icon: 'ri-customer-service-2-line',
  },
  {
    title: 'Usage tracking',
    copy: 'Keep voice minutes, plan usage, storage, and review activity visible before billing surprises.',
    icon: 'ri-bar-chart-grouped-line',
  },
  {
    title: 'Call summaries',
    copy: 'Turn each conversation into searchable context, tags, outcomes, and next steps.',
    icon: 'ri-file-list-3-line',
  },
];

const trustBlocks = [
  {
    title: 'Human judgment stays in the loop',
    copy: 'MiraSignal handles repetitive workflows and escalates calls when policy, urgency, or empathy needs a person.',
    icon: 'ri-user-follow-line',
  },
  {
    title: 'Every call leaves a record',
    copy: 'Transcripts, summaries, tags, tool actions, and handoff notes stay attached to the call history.',
    icon: 'ri-file-shield-2-line',
  },
  {
    title: 'Controlled workflows are easier to review',
    copy: 'Scripts, routing rules, manager visibility, configurable retention, and enterprise access options keep operations accountable.',
    icon: 'ri-lock-line',
  },
];

const faqs = [
  {
    question: 'Does MiraSignal replace human staff?',
    answer: 'No. It handles repetitive call workflows and escalates when human judgment, empathy, or approval is needed.',
  },
  {
    question: 'Can it handle after-hours calls?',
    answer: 'Yes. MiraSignal can answer after hours, collect intent, create a record, and queue the right follow-up for the team.',
  },
  {
    question: 'Can managers review calls?',
    answer: 'Yes. The product direction includes transcripts, summaries, outcomes, compliance notes, and call history for review.',
  },
  {
    question: 'Can it summarize calls?',
    answer: 'Yes. Calls can be turned into summaries, outcomes, tags, and next steps so operators are not starting from scratch.',
  },
  {
    question: 'Can it support outbound follow-ups?',
    answer: 'Yes. MiraSignal is positioned for inbound front desk coverage and outbound reminders, retries, and status calls.',
  },
  {
    question: 'How is pricing calculated?',
    answer: 'Pricing is based on expected call minutes, telephony, AI voice usage, summaries, logs, storage, retention, support, onboarding, and a safety buffer.',
  },
  {
    question: 'Can it be customized for my business?',
    answer: 'Yes. Scripts, FAQs, routing, escalation, business rules, and review requirements can be scoped to the workflow.',
  },
  {
    question: 'Is it suitable for clinics, service businesses, and agencies?',
    answer: 'Yes. Those are core use cases because missed calls, inconsistent follow-up, and weak call records are operational problems for those teams.',
  },
  {
    question: 'What happens when the AI cannot handle a call?',
    answer: 'The workflow should escalate to a human operator with caller intent, urgency, transcript context, and the latest summary attached.',
  },
];

const SectionHeader = ({ kicker, title, copy, centered = false }) => (
  <div className={`mira-section-head${centered ? ' centered' : ''}`}>
    {kicker && <span className="mira-kicker">{kicker}</span>}
    <h2>{title}</h2>
    {copy && <p>{copy}</p>}
  </div>
);

const IconBadge = ({ icon }) => (
  <span className="mira-icon-box">
    <i className={icon} aria-hidden="true"></i>
  </span>
);

const HeroCard = ({ card }) => (
  <div className={`mira-hero-card ${card.tone}`}>
    <span className="mira-card-icon">
      <i className={card.icon} aria-hidden="true"></i>
    </span>
    <span>
      <small>{card.label}</small>
      <strong>{card.value}</strong>
    </span>
  </div>
);

const HeroScene = () => (
  <div className="mira-hero-scene" aria-label="MiraSignal call operations preview">
    <div className="mira-scene-grid" aria-hidden="true"></div>
    <div className="mira-scene-status">
      <span className="mira-status-dot"></span>
      <span>Signal received</span>
    </div>

    <div className="mira-console-stage">
      <div className="mira-halo" aria-hidden="true"></div>
      <img
        className="mira-hero-console"
        src="/assets/img/brand/mira-hero-console.png"
        alt="MiraSignal desk phone console handling business call operations"
      />
    </div>

    <div className="mira-card-frame">
      {heroCards.map((card) => (
        <HeroCard card={card} key={card.label} />
      ))}
    </div>
  </div>
);

const TextLink = ({ href, children, variant = 'primary' }) => (
  <a className={`mira-button ${variant}`} href={href}>
    {children}
    {variant === 'primary' && <i className="ri-arrow-right-line" aria-hidden="true"></i>}
  </a>
);

const ProblemSection = () => (
  <section className="mira-section mira-problem" id="mirasignal-problem">
    <SectionHeader
      kicker="The problem"
      title="Your phone is already an operations channel. It just has no memory."
      copy="A call can contain urgency, revenue, consent, scheduling context, and compliance risk. Most teams still treat that information like a moment instead of a record."
    />

    <div className="mira-problem-grid">
      {problemPoints.map((item) => (
        <article className="mira-problem-card" key={item.title}>
          <IconBadge icon={item.icon} />
          <h3>{item.title}</h3>
          <p>{item.copy}</p>
        </article>
      ))}
    </div>
  </section>
);

const W6HSection = () => (
  <section className="mira-section mira-w6h" id="mirasignal-overview">
    <SectionHeader
      kicker="W6H snapshot"
      title="The product in plain operational terms."
      copy="MiraSignal is an AI voice operations layer for teams that cannot afford to miss, mishandle, or forget calls."
      centered
    />

    <div className="mira-w6h-grid">
      {w6hCards.map((card) => (
        <article className="mira-w6h-card" key={card.label}>
          <span>{card.label}</span>
          <h3>{card.title}</h3>
          <p>{card.copy}</p>
        </article>
      ))}
    </div>
  </section>
);

const LifecycleSection = () => (
  <section className="mira-section mira-lifecycle" id="how-it-works">
    <SectionHeader
      kicker="How it works"
      title="From ringing phone to operational record."
      copy="The workflow starts before the call is answered and ends only after the team can review what happened."
    />

    <div className="mira-lifecycle-track" aria-label="MiraSignal call lifecycle">
      {lifecycleSteps.map((step, index) => (
        <article className="mira-lifecycle-step" key={step.title}>
          <div className="mira-step-number">{String(index + 1).padStart(2, '0')}</div>
          <IconBadge icon={step.icon} />
          <h3>{step.title}</h3>
          <p>{step.copy}</p>
        </article>
      ))}
    </div>
  </section>
);

const FeaturesSection = () => (
  <section className="mira-section" id="mirasignal-features">
    <SectionHeader
      kicker="Product surface"
      title="Built around call handling, operator control, memory, and quality."
      copy="The product is not a chatbot bolted onto a phone line. It is a control layer around how calls become work."
    />

    <div className="mira-feature-grid">
      {featureGroups.map((group) => (
        <article className="mira-feature-card" key={group.title}>
          <div className="mira-feature-top">
            <IconBadge icon={group.icon} />
            <h3>{group.title}</h3>
          </div>
          <ul>
            {group.features.map((feature) => (
              <li key={feature}>{feature}</li>
            ))}
          </ul>
        </article>
      ))}
    </div>
  </section>
);

const PricingSection = () => (
  <section className="mira-pricing-band" id="mirasignal-pricing">
    <div className="mira-section">
      <div className="mira-pricing-intro">
        <SectionHeader
          kicker="Packages"
          title="Pricing should follow the call workload, not a guessed number."
          copy="The current product code has usage tracking and cost-estimate inputs for billable minutes, telephony, SMS, OpenAI realtime usage, and included minute limits. Live GCP/log usage was not accessible from this shell because the local token could not refresh non-interactively, so these packages avoid fake final prices."
        />

        <div className="mira-cost-card">
          <strong>Pricing inputs inspected</strong>
          <div className="mira-cost-list">
            {pricingInputs.map((input) => (
              <span key={input}>{input}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="mira-pricing-grid">
        {pricingPlans.map((plan) => (
          <article className={`mira-pricing-card${plan.highlighted ? ' highlighted' : ''}`} key={plan.name}>
            <div className="mira-plan-head">
              <span>{plan.bestFor}</span>
              <h3>{plan.name}</h3>
              <strong>{plan.pricing}</strong>
            </div>
            <p className="mira-plan-minutes">{plan.minutes}</p>
            <ul>
              {plan.features.map((feature) => (
                <li key={feature}>{feature}</li>
              ))}
            </ul>
            <p className="mira-plan-note">{plan.overage}</p>
            <p className="mira-plan-source">{plan.source}</p>
            <TextLink href={demoHref} variant={plan.highlighted ? 'primary' : 'secondary'}>
              {plan.cta}
            </TextLink>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const TestimonialsSection = () => (
  <section className="mira-section" id="mirasignal-testimonials">
    <SectionHeader
      kicker="Proof direction"
      title="Outcome themes to validate during pilots."
      copy="These are clearly marked placeholders until real customer quotes can be approved."
    />

    <div className="mira-testimonial-grid">
      {testimonials.map((item) => (
        <article className="mira-testimonial-card" key={`${item.label}-${item.context}`}>
          <span>{item.label}</span>
          <blockquote>{item.quote}</blockquote>
          <p>{item.context}</p>
        </article>
      ))}
    </div>
  </section>
);

const UseCasesSection = () => (
  <section className="mira-section" id="mirasignal-use-cases">
    <SectionHeader
      kicker="Use cases"
      title="Specific call operations that need memory and follow-through."
      copy="MiraSignal fits teams where the phone is already connected to revenue, trust, service quality, or compliance."
    />

    <div className="mira-use-grid">
      {useCases.map((item) => (
        <article className="mira-use-card" key={item.title}>
          <IconBadge icon={item.icon} />
          <h3>{item.title}</h3>
          <p>{item.copy}</p>
        </article>
      ))}
    </div>
  </section>
);

const DashboardPreview = () => (
  <section className="mira-ops" id="mirasignal-product">
    <div className="mira-ops-inner">
      <SectionHeader
        kicker="Operator preview"
        title="A SaaS control room for call operations."
        copy="The dashboard preview mirrors the real product direction: sidebar navigation, plan usage, live activity, summaries, compliance state, and handoff visibility in one clean workspace."
        centered
      />

      <div className="mira-board-shell">
        <div className="mira-board" aria-label="MiraSignal operator dashboard preview">
          <aside className="mira-board-sidebar" aria-label="Preview navigation">
            <div className="mira-board-logo">
              <span>MS</span>
              <strong>MiraSignal</strong>
            </div>
            {['Dashboard', 'Users', 'Agents', 'Plans', 'Invoices', 'Settings'].map((item, index) => (
              <span className={index === 0 ? 'active' : ''} key={item}>{item}</span>
            ))}
            <div className="mira-board-account">
              <span>Sample workspace</span>
              <a href={appUrl}>Client login</a>
            </div>
          </aside>

          <div className="mira-board-workspace">
            <div className="mira-board-top">
              <div>
                <strong>Dashboard</strong>
                <span>Call operation overview</span>
              </div>
              <label className="mira-board-search">
                <i className="ri-search-line" aria-hidden="true"></i>
                <span>Search calls, agents, workflows</span>
              </label>
            </div>

            <div className="mira-board-welcome">
              <div>
                <h3>Welcome back</h3>
                <p>Here is your MiraSignal call operation overview.</p>
              </div>
              <div className="mira-board-callout">
                <span>Live review</span>
                <strong>4 calls need attention</strong>
                <small>Handoff, summary, and compliance queues</small>
              </div>
            </div>

            <div className="mira-board-stats">
              {dashboardStats.map((stat) => (
                <div className={`mira-board-stat ${stat.tone}`} key={stat.label}>
                  <span>{stat.label}</span>
                  <strong>{stat.value}</strong>
                </div>
              ))}
            </div>

            <div className="mira-board-main-grid">
              <div>
                <div className="mira-board-section-title">
                  <strong>Plan Details and Usage</strong>
                  <span>Sample preview data</span>
                </div>

                <div className="mira-board-usage-grid">
                  {dashboardUsageCards.map((card) => (
                    <article className="mira-board-usage-card" key={card.title}>
                      <div>
                        <strong>{card.title}</strong>
                        <em>{card.status}</em>
                      </div>
                      {card.rows.map(([label, value]) => (
                        <p key={label}>
                          <span>{label}</span>
                          <b>{value}</b>
                        </p>
                      ))}
                      {card.progress === null ? (
                        <div className="mira-board-progress unavailable"><span>Usage meter pending</span></div>
                      ) : (
                        <div className="mira-board-progress" aria-label={`${card.title} usage ${card.progress}%`}>
                          <span style={{ width: `${card.progress}%` }}></span>
                        </div>
                      )}
                      <small>{card.note}</small>
                    </article>
                  ))}
                </div>
              </div>

              <div className="mira-board-side-stack">
                <div className="mira-summary-panel">
                  <div>
                    <span className="mira-status-dot"></span>
                    <strong>Latest call summary</strong>
                  </div>
                  <p>
                    Sample caller needs a same-week appointment, prefers afternoon slots, asked about
                    coverage, and should be transferred if no slot is available.
                  </p>
                </div>

                <div className="mira-board-rows">
                  {dashboardRows.map((row) => (
                    <div className="mira-board-row" key={row.label}>
                      <span>
                        <strong>{row.label}</strong>
                        <small>{row.detail}</small>
                      </span>
                      <em className={row.tone}>{row.status}</em>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mira-board-support-grid">
          {dashboardSupportCards.map((card) => (
            <article className="mira-board-support-card" key={card.title}>
              <IconBadge icon={card.icon} />
              <h3>{card.title}</h3>
              <p>{card.copy}</p>
            </article>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const TrustSection = () => (
  <section className="mira-section mira-trust" id="mirasignal-trust">
    <SectionHeader
      kicker="Trust and control"
      title="Designed for handoff, review, and accountability."
      copy="MiraSignal should make the team more visible and better prepared, not remove the people who need to make judgment calls."
      centered
    />

    <div className="mira-trust-grid">
      {trustBlocks.map((item) => (
        <article className="mira-trust-card" key={item.title}>
          <IconBadge icon={item.icon} />
          <h3>{item.title}</h3>
          <p>{item.copy}</p>
        </article>
      ))}
    </div>
  </section>
);

const FaqSection = () => (
  <section className="mira-section mira-faq" id="mirasignal-faq">
    <SectionHeader
      kicker="FAQ"
      title="Questions a serious call operations buyer will ask."
      copy="Short answers for the first review. The demo can go deeper into scripts, escalation, compliance, and usage modeling."
    />

    <div className="mira-faq-grid">
      {faqs.map((faq) => (
        <article className="mira-faq-item" key={faq.question}>
          <h3>{faq.question}</h3>
          <p>{faq.answer}</p>
        </article>
      ))}
    </div>
  </section>
);

const FinalCta = () => (
  <section className="mira-final-cta">
    <div>
      <span className="mira-kicker">Call records your team can trust</span>
      <h2>Stop letting calls disappear after they ring.</h2>
      <p>
        Scope the workflow, estimate usage, and decide where AI should answer, route, summarize,
        review, or hand off to a person.
      </p>
      <div className="mira-actions">
        <TextLink href={demoHref}>Book a demo</TextLink>
        <TextLink href={demoHref} variant="secondary">Discuss my call workflow</TextLink>
      </div>
    </div>
    <div className="mira-cta-console" aria-label="MiraSignal console ready for the next call">
      <img src="/assets/img/brand/mira-small-console.png" alt="MiraSignal console ready for the next call" />
      <span>Record saved</span>
    </div>
  </section>
);

const MiraSignalPage = ({ darkTheme, toggleTheme }) => (
  <>
    <Header darkTheme={darkTheme} toggleTheme={toggleTheme} />

    <main className="mira-page">
      <section className="mira-hero" aria-labelledby="mirasignal-title">
        <div className="mira-hero-inner">
          <div className="mira-hero-copy">
            <span className="mira-kicker">AI voice operations</span>
            <h1 id="mirasignal-title">Turn every business call into a logged, summarized, reviewable workflow.</h1>
            <p>
              MiraSignal answers, qualifies, routes, summarizes, and audits calls so your team can
              stop losing revenue in voicemail, missed calls, and forgotten follow-ups.
            </p>

            <div className="mira-actions">
              <TextLink href={demoHref}>Book a demo</TextLink>
              <TextLink href="#how-it-works" variant="secondary">See how it works</TextLink>
              <TextLink href={appUrl} variant="ghost">Open app</TextLink>
            </div>

            <div className="mira-trust-strip" aria-label="MiraSignal operational trust indicators">
              {trustIndicators.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
          </div>

          <HeroScene />
        </div>
      </section>

      <ProblemSection />
      <W6HSection />
      <LifecycleSection />
      <FeaturesSection />
      <PricingSection />
      <TestimonialsSection />
      <UseCasesSection />
      <DashboardPreview />
      <TrustSection />
      <FaqSection />
      <FinalCta />
    </main>

    <Footer />
    <ScrollUp />
  </>
);

export default MiraSignalPage;
